import { ChevronDown, Settings, FlaskConical } from 'lucide-react';
import { Button } from './ui/button';
import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { MobileNav } from '../portal/MobileNav';
import { Property } from '../../components/Investment/PropertyCard';
import { PropertyCardData } from './PropertyCard';
import { useUser } from '../../contexts/UserContext';
import { confirmSignOutWithUnsent } from '../../config/authRoutes';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { BRAND_ASSETS, TENANT } from '../../config';
import { AccountSetupBanner } from '../Auth/AccountSetupBanner';
import { buildOnboardingFromUser } from '../../utils/onboardingFromUser';
import { stepToDestination } from '../../utils/onboarding.config';
import { CONTENT_WIDTH } from './layout.constants';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { SchedulerModal } from './SchedulerModal';

const SHOW_FUNDING = TENANT.features.crypto;
const SHOW_SHARE_CONVERSION = TENANT.features.shareConversion;

// Helper function to convert Property to PropertyCardData
// Handles both snake_case (API) and camelCase field names
export const convertToPropertyCardData = (property: Property & Record<string, unknown>): PropertyCardData => {
  // Handle both API (snake_case) and legacy (camelCase) field names
  const price = (property as Record<string, unknown>).total_value as number || property.price || 0;
  const totalShares = (property as Record<string, unknown>).total_shares as number || property.tokenSupply || 1;
  const availableShares = (property as Record<string, unknown>).available_shares as number || totalShares - (property.investors || 0);
  const targetReturn = (property as Record<string, unknown>).annual_yield as number || property.expectedRoi || 0;
  const investorCount = (property as Record<string, unknown>).stakersCount as number || property.investors || 0;
  const images = (property as Record<string, unknown>).images as string[] | undefined;
  const imageUrl = images?.[0] || property.imageUrl || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&auto=format&fit=crop&q=80';
  const propertyType = (property as Record<string, unknown>).property_type as string || property.propertyType || 'single-family';
  
  const fundedPercentage = totalShares > 0 ? Math.round(((totalShares - availableShares) / totalShares) * 100) : 0;
  
  // Determine status - handle both legacy and API status values
  const rawStatus = (property as Record<string, unknown>).status as string;
  const mappedStatus: 'available' | 'funded' | 'sold_out' = 
    rawStatus === 'available' || rawStatus === 'active' ? 'available' : 'funded';
  
  return {
    id: property.id,
    name: property.address || (property as Record<string, unknown>).title as string || 'Property',
    address: property.address,
    city: property.city,
    state: property.state,
    imageUrl,
    propertyType,
    price,
    totalShares,
    availableShares,
    targetReturn,
    investorCount,
    fundedPercentage,
    status: mappedStatus,
    tags: [],
    fieldVisibility: ((property as Record<string, unknown>).propertyData as any)?.fieldVisibility
      ?? ((property as Record<string, unknown>).property_data as any)?.fieldVisibility,
  };
};

export function InvestorPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, refreshUser } = useUser();

  // Scheduler opens as a modal over the current page (not a route) so it never becomes a
  // browser-history entry — hitting Back after scheduling shouldn't return to the scheduler.
  const [schedulerOpen, setSchedulerOpen] = useState(false);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  // The mobile nav (and its scroll lock) only exists below `md`. If the viewport crosses to
  // desktop while it's open — e.g. widening a narrowed window — close it, so it doesn't linger
  // as a hidden-but-open panel keeping the background scroll locked.
  const isDesktop = useIsDesktop();
  useEffect(() => {
    if (isDesktop) setIsMenuOpen(false);
  }, [isDesktop]);

  // Sandbox mode state — persists from admin toggle via localStorage
  const [sandboxPayments, setSandboxPayments] = useState(() =>
    localStorage.getItem('sandboxPayments') === 'true'
  );
  const isAdmin = user?.role === 'admin';

  const toggleSandboxPayments = () => {
    const next = !sandboxPayments;
    setSandboxPayments(next);
    localStorage.setItem('sandboxPayments', String(next));
  };

  // Onboarding banner — derived from the authenticated user's status fields so it works
  // on this backend (no dedicated onboarding-status endpoint). Hidden for admins and,
  // via AccountSetupBanner, once every step is complete.
  const onboardingData = useMemo(
    () => (user && user.role !== 'admin' ? buildOnboardingFromUser(user) : null),
    [user],
  );

  // --invest-filter-top: where in-page sticky bars (the sticky category capsules) pin —
  // below the header, plus the condensed onboarding banner's height when that banner is
  // active. Both are measured (header via ref, condensed via the banner's callback), so
  // there are no hard-coded pixel offsets.
  const headerRef = useRef<HTMLElement>(null);
  const [headerH, setHeaderH] = useState(0);
  const [condensedH, setCondensedH] = useState(0);
  // Measure before paint so the sticky filter offset is correct on the first frame (avoids a
  // transient 0px top). Re-measure on resize as the header height can change with breakpoints.
  useLayoutEffect(() => {
    const measure = () => { if (headerRef.current) setHeaderH(headerRef.current.getBoundingClientRect().height); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const bannerActive = !!onboardingData;
  const filterStickyTop = Math.round(headerH) + (bannerActive ? Math.round(condensedH) : 0);

  // Dismiss the desktop account popover on any click outside it, or on Escape.
  useEffect(() => {
    if (!userDropdownOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserDropdownOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [userDropdownOpen]);

  const hideOnboardingBanner = location.pathname.startsWith('/portal/onboarding-call');

  const handleLogout = async () => {
    if (!confirmSignOutWithUnsent(user?.id)) return; // unsent notes on this computer: ask first
    await logout();
    navigate('/');
  };

  const handleOnboardingStepClick = (stepId: string) => {
    // Scheduling is a transient task — open it as a modal over context instead of routing.
    if (stepId === 'schedule-meeting') {
      setSchedulerOpen(true);
      return;
    }
    // Other destinations live in the onboarding config (single source of truth).
    navigate(stepToDestination(stepId));
  };

  const closeScheduler = () => {
    setSchedulerOpen(false);
    // A booking may have advanced onboarding — refresh the user so the banner reflects it.
    refreshUser();
  };

  const isActiveSection = (section: 'invest' | 'portfolio' | 'funding' | 'share-conversion' | 'payments'): boolean => {
    if (section === 'invest') {
      return location.pathname.startsWith('/portal/invest');
    }
    if (section === 'portfolio') {
      return location.pathname === '/portal/portfolio';
    }
    if (section === 'funding') {
      return location.pathname === '/portal/funding';
    }
    if (section === 'share-conversion') {
      return location.pathname === '/portal/share-conversion';
    }
    if (section === 'payments') {
      return location.pathname.startsWith('/portal/payments');
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-background portal-typography" style={{ ['--invest-filter-top' as any]: `${filterStickyTop}px` }}>
      {/* Header */}
      <header ref={headerRef} className="border-b border-border bg-[#F0F0F0] sticky top-0 z-50">
        <div className={`${CONTENT_WIDTH} py-3`}>
          <div className="flex items-center justify-start gap-8">
            {/* Logo - Larger with Poppins Light */}
            <div className="flex items-center flex-shrink-0">
              <img
                src={BRAND_ASSETS.logo.primary}
                alt={`${TENANT.branding.name} Logo`}
                className="h-12 w-auto cursor-pointer font-logo"
                onClick={() => navigate('/portal/invest/properties')}
              />
            </div>

            {/* Desktop Navigation - Leading aligned */}
            <nav className="hidden md:flex items-center gap-6">
              <button
                onClick={() => navigate('/portal/invest/properties')}
                className={`py-2 text-base font-medium transition-all relative ${
                  isActiveSection('invest')
                    ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Invest
              </button>
              <button
                onClick={() => navigate('/portal/portfolio')}
                className={`py-2 text-base font-medium transition-all relative ${
                  isActiveSection('portfolio')
                    ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Portfolio
              </button>
              {SHOW_FUNDING && (
                <button
                  onClick={() => navigate('/portal/funding')}
                  className={`py-2 text-base font-medium transition-all relative ${
                    isActiveSection('funding')
                      ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Funding
                </button>
              )}
              {SHOW_SHARE_CONVERSION && (
                <button
                  onClick={() => navigate('/portal/share-conversion')}
                  className={`py-2 text-base font-medium transition-all relative ${
                    isActiveSection('share-conversion')
                      ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Share Conversion
                </button>
              )}
              <button
                onClick={() => navigate('/portal/payments')}
                className={`py-2 text-base font-medium transition-all relative ${
                  isActiveSection('payments')
                    ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Transactions
              </button>
            </nav>

            {/* User dropdown on the right - Desktop */}
            <div ref={userDropdownRef} className="hidden md:flex items-center gap-2 relative ml-auto">
              {/* Sandbox toggle — admin only */}
              {isAdmin && (
                <button
                  onClick={toggleSandboxPayments}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    sandboxPayments
                      ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                  title={sandboxPayments ? 'Sandbox ON — test credentials active, KYC/accreditation bypassed' : 'Click to enable sandbox mode'}
                >
                  <FlaskConical className="w-3 h-3" />
                  {sandboxPayments ? 'Sandbox ON' : 'Sandbox'}
                </button>
              )}
              <Button
                variant="ghost"
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center gap-2 text-foreground hover:bg-transparent dark:hover:bg-transparent hover:text-foreground/60"
              >
                <span>{user?.email?.split('@')[0] || 'User'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${userDropdownOpen ? 'rotate-180' : ''}`} />
              </Button>

              {userDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-xl shadow-lg overflow-hidden z-50">
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setUserDropdownOpen(false);
                      }}
                      className="w-full px-4 py-3 text-left text-sm font-medium text-foreground hover:bg-muted transition-colors"
                    >
                      Admin Portal
                    </button>
                  )}
                  <button
                    onClick={() => {
                      navigate('/portal/settings');
                      setUserDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left text-sm font-medium text-foreground border-t border-border first:border-t-0 hover:bg-muted transition-colors"
                  >
                    Account Settings
                  </button>
                  <button
                    onClick={() => {
                      setUserDropdownOpen(false);
                      handleLogout();
                    }}
                    className="w-full px-4 py-3 text-left text-sm font-medium text-foreground border-t border-border first:border-t-0 hover:bg-muted transition-colors"
                  >
                    Log Out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu — hamburger that morphs into an X, part of the tab bar (md:hidden) */}
            <div className="flex items-center md:hidden ml-auto">
              <button
                type="button"
                onClick={() => setIsMenuOpen((o) => !o)}
                aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={isMenuOpen}
                className="relative flex h-10 w-10 items-center justify-center text-foreground"
              >
                <span className="relative block h-4 w-6" aria-hidden="true">
                  <span
                    className={`absolute left-0 h-0.5 w-6 rounded-full bg-current transition-all duration-300 ${
                      isMenuOpen ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0'
                    }`}
                  />
                  <span
                    className={`absolute left-0 top-1/2 -translate-y-1/2 h-0.5 w-6 rounded-full bg-current transition-opacity duration-300 ${
                      isMenuOpen ? 'opacity-0' : 'opacity-100'
                    }`}
                  />
                  <span
                    className={`absolute left-0 h-0.5 w-6 rounded-full bg-current transition-all duration-300 ${
                      isMenuOpen ? 'bottom-1/2 translate-y-1/2 -rotate-45' : 'bottom-0'
                    }`}
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Sandbox mode banner — visible to admins when sandbox is active */}
      {isAdmin && sandboxPayments && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-800 text-sm">
              <FlaskConical className="w-4 h-4" />
              <span className="font-semibold">Sandbox Mode</span>
              <span className="text-amber-600">&mdash; KYC &amp; accreditation bypassed. Payments use test credentials. Transactions are not real.</span>
            </div>
            <button
              onClick={toggleSandboxPayments}
              className="text-xs text-amber-700 hover:text-amber-900 underline font-medium"
            >
              Disable
            </button>
          </div>
        </div>
      )}

      {/* Mobile Navigation — full-screen overlay (Arrived-style). Primary sections are text
          rows; Settings + Log out live in the pinned account block. */}
      <MobileNav
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        navItems={[
          { label: 'Invest', href: '/portal/invest/properties' },
          { label: 'Portfolio', href: '/portal/portfolio' },
          ...(SHOW_FUNDING ? [{ label: 'Funding', href: '/portal/funding' }] : []),
          ...(SHOW_SHARE_CONVERSION ? [{ label: 'Share Conversion', href: '/portal/share-conversion' }] : []),
          { label: 'Transactions', href: '/portal/payments' },
          ...(user?.role === 'admin' ? [{ label: 'Admin Portal', href: '/admin' }] : []),
        ]}
        accountItems={[
          { label: 'Settings', href: '/portal/settings', icon: <Settings className="w-4 h-4" /> },
        ]}
        user={user ? { name: user.email?.split('@')[0] || 'User', email: user.email || '' } : null}
        onLogout={handleLogout}
        topOffset={Math.round(headerH)}
      />

      {/* Onboarding Banner - appears below nav, above content. Shows the "You're all set" success
          state when complete (dismissible); collapses to nothing (empty:hidden) once dismissed. */}
      {bannerActive && !hideOnboardingBanner && (
        <div className={`${CONTENT_WIDTH} pt-6 md:pt-8 empty:hidden`}>
          <AccountSetupBanner
            onboarding={onboardingData}
            onStepClick={handleOnboardingStepClick}
            stickyCondensed={!location.pathname.startsWith('/portal/invest/properties/')}
            onCondensedHeight={setCondensedH}
            headerHeight={Math.round(headerH)}
            dismissKey={user ? `onboardingSuccessDismissed:${user.id}` : undefined}
          />
        </div>
      )}

      {/* Route-driven content. Each page owns its own <PortalContainer> (width + gutters +
          vertical rhythm), so the shell no longer wraps the outlet. */}
      <Outlet />

      {/* Onboarding call scheduler — modal over context (see schedulerOpen). Deep links still
          use the /portal/onboarding-call route, which renders the same flow as a full page. */}
      <SchedulerModal
        open={schedulerOpen}
        onOpenChange={(open) => (open ? setSchedulerOpen(true) : closeScheduler())}
        onClose={closeScheduler}
        source="onboarding-banner"
      />
    </div>
  );
}
